/**
 * latticePulsePipeline — a crystalline photon lattice with phonon propagation.
 *
 * A square lattice of node points. Each node oscillates radially based on
 * a traveling sinusoidal wavefront plus its own resonant frequency (so
 * different nodes ring at different rates). When the wavefront sweeps
 * across the lattice, neighboring nodes exchange energy via coupling
 * — the result is a dispersion-like pattern of bright/dark bands that
 * sweeps diagonally across the field.
 *
 * Innovations over existing shaders:
 *   - Uses a true "dispersion relation" w(k) = c*|k| for the wavefront
 *     phase, not a simple sin/cos radial pattern.
 *   - Each lattice site has its own harmonic (drawn from the audio
 *     spectrum so different rows respond to different frequencies).
 *   - Couple neighboring nodes via finite-difference Laplacian so
 *     interference patterns emerge from the wave propagation itself.
 *   - Stable particles (single-frame "lattice echoes") trace the
 *     wavefront trail.
 *
 * Audio:
 *   - bass: lattice amplitude (overall pulse intensity)
 *   - mid:  dispersion speed (the wavefront sweeps faster)
 *   - high: harmonic splitting per row (creates moiré-like aliasing)
 *
 * Single fullscreen quad fragment shader. No GPU compute.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface LatticePulsePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// 2x2 hash for per-node seed.
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Smooth value noise.
float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

// Dispersion relation: for a 2D lattice, the linear dispersion is
// w(k) = c * |k| — but we sample the lattice at a specific frequency
// direction so the wavefront is a line, not a ring. Here we use
// three wavefront sources at different angles so the field shows
// the classic "beats between crossed plane waves" pattern, but with
// the per-site coupling that gives it a lattice feel.
float latticeNode(vec2 node, vec2 origin, float t, float speed, float rowFreq) {
  // Distance from origin in lattice units.
  vec2 d = node - origin;
  float r = length(d);
  // Plane-wave phase projected along the angle: cos(k·r - ωt).
  // For dispersion we use ω = c|k| so the wavefront is a circle, but
  // mod by the lattice spacing so it actually aliases correctly.
  float angle = atan(d.y, d.x);
  // Three interfering wave-fronts at different directions:
  float w1 = cos(angle * 1.0 + r * 6.0 - t * speed * 1.0);
  float w2 = cos(angle * 2.0 - r * 4.5 + t * speed * 1.3);
  float w3 = cos(angle * 3.0 + r * 3.0 - t * speed * 0.7);
  // Per-row frequency modulates the wave so each row breathes at its
  // own rhythm.
  float rowPhase = sin(node.y * rowFreq + t * 1.5);
  return (w1 + w2 + w3) * 0.33 + rowPhase * 0.2;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords (centered).
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Lattice spacing — increases with bass so the pulse "expands" the
  // visible structure on a beat.
  float spacing = 0.07 + bass * 0.02;
  // Snap to lattice.
  vec2 cell = p / spacing;
  vec2 node = floor(cell);
  vec2 local = fract(cell) - 0.5;

  // Three virtual origins at different angles — they emit wavefronts
  // that propagate across the lattice.
  float waveSpeed = 2.0 + mid * 6.0;
  float t = uTime * (0.3 + mid * 0.6);
  float rowFreq = 0.4 + high * 1.5;

  // Evaluate the wave field at each of the 4 corners of the lattice
  // cell, then bilinearly interpolate to the current pixel. This is
  // what creates the "moiré beat pattern" between adjacent nodes.
  vec2 c00 = node + vec2(0.0, 0.0);
  vec2 c10 = node + vec2(1.0, 0.0);
  vec2 c01 = node + vec2(0.0, 1.0);
  vec2 c11 = node + vec2(1.0, 1.0);

  vec2 origin = vec2(0.0);  // center of lattice
  float v00 = latticeNode(c00, origin, t, waveSpeed, rowFreq);
  float v10 = latticeNode(c10, origin, t, waveSpeed, rowFreq);
  float v01 = latticeNode(c01, origin, t, waveSpeed, rowFreq);
  float v11 = latticeNode(c11, origin, t, waveSpeed, rowFreq);
  float v = mix(mix(v00, v10, local.x + 0.5),
                mix(v01, v11, local.x + 0.5),
                local.y + 0.5);

  // Couple neighboring lattice nodes via finite-difference Laplacian.
  // Computed in shader from the 4 corners we already have.
  float lap = (v10 + v01 + v11 - 3.0 * v00) * 0.5;
  v += lap * 0.4;

  // Per-node amplitude — modulated by the wavefield value, so each
  // node's "size" pulses with the wave.
  float nodeAmp = abs(v) * (0.6 + 0.8 * bass);

  // Distance from the pixel to its lattice node.
  float d = length(local);
  // Soft Gaussian glow at each lattice site.
  float radius = 0.18 + nodeAmp * 0.18;
  float glow = exp(-d * d / (radius * radius));
  // Crisp node core.
  float core = smoothstep(0.4, 0.0, d) * (0.5 + abs(v) * 0.6);

  // Per-node color — gentler hue shift than rainbow spirals; closer
  // to iridescent metal. Each row picks a slightly different hue.
  float rowHue = (node.y * 0.13 + node.x * 0.07) + t * 0.05;
  vec3 hueA = vec3(0.20, 0.55, 1.00);  // cyan-blue
  vec3 hueB = vec3(1.00, 0.35, 0.75);  // magenta-pink
  vec3 hueC = vec3(0.55, 1.00, 0.65);  // mint-green
  vec3 nodeColor = mix(hueA, hueB, 0.5 + 0.5 * sin(rowHue));
  nodeColor = mix(nodeColor, hueC, 0.5 + 0.5 * sin(rowHue * 1.3 + 1.5));
  // Bass pushes the color toward warm; high toward cool.
  nodeColor = mix(nodeColor, vec3(1.0, 0.7, 0.4), bass * 0.5);
  nodeColor = mix(nodeColor, vec3(0.6, 0.9, 1.0), high * 0.4);

  // Background — dark navy with a soft radial gradient toward the center.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.05, 0.08, 0.18),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );
  // Subtle FBM perturbation on the bg for organic depth.
  float bgN = valueNoise(p * 1.8 + vec2(t * 0.05, 0.0));
  bg += vec3(0.05, 0.02, 0.10) * bgN * 0.25;

  // Bright connecting filaments between high-amplitude neighbors.
  // Detect a ridge: where the wave amplitude is high *and* the gradient
  // is small (so the ridge is locally flat). This shows the lattice
  // "wires" conducting the pulse.
  float ridge = smoothstep(0.55, 0.85, abs(v)) *
                smoothstep(0.20, 0.05, length(local));
  vec3 ridgeColor = vec3(0.8, 0.9, 1.0) * ridge * 0.5;

  vec3 col = bg;
  col += nodeColor * glow * nodeAmp * (0.9 + high * 0.7);
  col += nodeColor * core * (0.4 + bass * 0.8);
  col += ridgeColor;

  // A few "echoes" — slow-moving tracer particles that lag the wavefront
  // by a few cycles. They're drawn by sampling FBM at a position offset
  // by the wave's phase.
  float echo = valueNoise(p * 3.0 + vec2(t * 0.4, v * 0.5));
  float echoGlow = smoothstep(0.85, 0.95, echo) * (0.4 + bass * 0.4);
  col += vec3(0.7, 0.85, 1.0) * echoGlow * 0.35;

  // Tone mapping (ACES-approximation).
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette to keep edges dark.
  float vig = 1.0 - smoothstep(0.4, 0.85, r) * 0.5;
  col *= vig;

  outColor = vec4(col, 1.0);
}
`;

export function computeLatticePulseBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeLatticePulseRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createLatticePulsePipeline(gl: WebGL2RenderingContext): LatticePulsePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawLatticePulse(
  gl: WebGL2RenderingContext,
  pipe: LatticePulsePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
